import {
  AppWindow,
  Building2,
  Layers,
  LayoutDashboard,
  Signature,
} from "lucide-react";

import { Sidebar, useSidebar } from "@/components/ui";
import { AppsSubMenu } from "@/components/ui/AppSidebar/AppsSubMenu";
import { ContactsSubMenu } from "@/components/ui/AppSidebar/ContactsSubMenu";
import { FirmSubMenu } from "@/components/ui/AppSidebar/FirmSubMenu";
import { IntakeSubMenu } from "@/components/ui/AppSidebar/IntakeSubMenu";
import { MassTortsSubMenu } from "@/components/ui/AppSidebar/MassTortsSubMenu";
import { MattersSubMenu } from "@/components/ui/AppSidebar/MattersSubMenu";
import { ReferralsSubMenu } from "@/components/ui/AppSidebar/ReferralsSubMenu";
import { useUser } from "@/components/UserProvider";
import { PAGE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import { useQuery } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import {
  ContactIcon,
  FileIcon,
  MatterIcon,
  OpportunityIcon,
  ReferralIcon,
} from "@/lib/utils/iconUtils";
import { useMatches } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { SidebarSectionRail } from "./SidebarSectionRail";
import { SidebarSectionPanel } from "./SidebarSectionPanel";
import { useSidebarNavigation } from "./useSidebarNavigation";
import {
  canRestoreSidebarPage,
  getFirstSidebarPage,
} from "./sidebar-destinations";
import {
  useSidebarPageCache,
  type SidebarDestination,
} from "./useSidebarPageCache";

const MATTER_SETTINGS_SECTION_PATHS = [
  "/matters/settings",
  "/settings/matter-defaults",
] as const;

const FIRM_SECTION_PATHS = [
  "/settings/firm",
  "/settings/files",
  "/settings/templates",
  "/settings/users",
  "/settings/communication",
  "/profile",
  "/reports/user-productivity",
  "/charges",
] as const;

const isAnyPathMatch = (path: string, prefixes: readonly string[]) =>
  prefixes.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));

export function AppSidebar() {
  const { currentUser, hasAnyPagePermission, hasPagePermission } = useUser();
  const { state, isMobile } = useSidebar();
  const isCollapsed = !isMobile && state === "collapsed";

  const matches = useMatches();
  const matterId = (
    matches.find((match) => match.pathname.startsWith("/matters/")) as
      | { params?: { matterId?: string } }
      | undefined
  )?.params?.matterId;
  const massTortSearchId = (
    matches.find((match) => match.pathname.startsWith("/mass-torts/")) as
      | { params?: { massTortSearchId?: string } }
      | undefined
  )?.params?.massTortSearchId;
  const currentPath = matches[matches.length - 1].pathname;
  const {
    data: matterResponse,
    isPending: isMatterPending,
    isSuccess: isMatterSuccess,
    error: matterError,
  } = useQuery(retrieveMatterQueryOptions(matterId ?? ""));
  const matter = Array.isArray(matterResponse?.data)
    ? undefined
    : matterResponse?.data;
  const isMatterNotFound =
    Boolean(matterId) &&
    ((isAxiosError(matterError) && matterError.response?.status === 404) ||
      (isMatterSuccess && !matter?.id));
  // Mounting a failed detail query retries it, which would repeatedly hide and
  // remount the submenu. Keep list navigation available without that query.
  const availableMatterId =
    !isMatterNotFound && matter?.id ? matterId : undefined;
  const activeMatterMassTortId =
    availableMatterId && hasPagePermission(PAGE_PERMISSION_KEYS.massTortsAccess)
      ? (matter?.massTortId ?? undefined)
      : undefined;
  const isMatterContextPending = Boolean(matterId) && isMatterPending;

  const isIntakeActive =
    currentPath === "/leads" ||
    currentPath.startsWith("/leads/") ||
    currentPath === "/opportunities" ||
    currentPath.startsWith("/opportunities/") ||
    currentPath.startsWith("/settings/intake");
  const isMattersActive =
    (currentPath.startsWith("/matters") && !activeMatterMassTortId) ||
    isAnyPathMatch(currentPath, MATTER_SETTINGS_SECTION_PATHS) ||
    (currentPath.startsWith("/reports") &&
      currentPath !== "/reports/user-productivity");
  const isReferralsActive = currentPath.startsWith("/referrals");
  const isMassTortsActive =
    currentPath.startsWith("/mass-torts") || Boolean(activeMatterMassTortId);
  const isContactsActive =
    currentPath.startsWith("/contacts") ||
    currentPath.startsWith("/settings/contact");
  const isAppsActive = currentPath.startsWith("/apps");
  const isFirmActive = isAnyPathMatch(currentPath, FIRM_SECTION_PATHS);

  const canAccessSignatureRequests = hasPagePermission(
    PAGE_PERMISSION_KEYS.signatureRequestsAccess,
  );
  const canAccessApps = hasPagePermission(PAGE_PERMISSION_KEYS.appsAccess);

  const items = [
    {
      title: "Dashboard",
      icon: LayoutDashboard,
      access: hasPagePermission(PAGE_PERMISSION_KEYS.dashboard),
      active: currentPath === "/",
      submenu: null,
    },
    {
      title: "Intake",
      icon: OpportunityIcon,
      access: hasPagePermission(PAGE_PERMISSION_KEYS.intakeAccess),
      active: isIntakeActive,
      submenu: <IntakeSubMenu currentPath={currentPath} />,
    },
    {
      title: "Matters",
      icon: MatterIcon,
      access: hasPagePermission(PAGE_PERMISSION_KEYS.mattersAccess),
      active: isMattersActive,
      submenu: (
        <MattersSubMenu
          matterId={activeMatterMassTortId ? undefined : availableMatterId}
          currentPath={currentPath}
        />
      ),
    },
    {
      title: "Referrals",
      icon: ReferralIcon,
      access: hasPagePermission(PAGE_PERMISSION_KEYS.referralsAccess),
      active: isReferralsActive,
      submenu: <ReferralsSubMenu currentPath={currentPath} />,
    },
    {
      title: "Mass Torts",
      label: "Mass torts",
      icon: Layers,
      access: hasPagePermission(PAGE_PERMISSION_KEYS.massTortsAccess),
      active: isMassTortsActive,
      submenu: (
        <MassTortsSubMenu
          massTortSearchId={massTortSearchId ?? activeMatterMassTortId}
          matterId={activeMatterMassTortId ? availableMatterId : undefined}
          currentPath={currentPath}
        />
      ),
    },
    {
      title: "Files",
      icon: FileIcon,
      access: true,
      active: currentPath === "/files",
      submenu: null,
    },
    {
      title: "Signature Requests",
      label: "Signature requests",
      icon: Signature,
      access: canAccessSignatureRequests,
      active: currentPath === "/signature-requests",
      submenu: null,
    },
    {
      title: "Contacts",
      icon: ContactIcon,
      access: hasPagePermission(PAGE_PERMISSION_KEYS.contactsAccess),
      active: isContactsActive,
      submenu: <ContactsSubMenu currentPath={currentPath} />,
    },
    {
      title: "Apps",
      icon: AppWindow,
      access: canAccessApps,
      active: isAppsActive,
      submenu: <AppsSubMenu currentPath={currentPath} />,
    },
    {
      title: "Firm",
      icon: Building2,
      access: hasAnyPagePermission([
        PAGE_PERMISSION_KEYS.firmAccess,
        PAGE_PERMISSION_KEYS.firmSettings,
      ]),
      active: isFirmActive,
      submenu: <FirmSubMenu currentPath={currentPath} />,
    },
  ].filter((item) => item.access);

  const routeSection = items.find((item) => item.active)?.title;
  const scope = JSON.stringify([currentUser.firmId, currentUser.id]);
  const rememberedPages = useSidebarPageCache(
    scope,
    matterId && (!isMatterSuccess || !availableMatterId)
      ? undefined
      : routeSection,
    isMatterNotFound ? `/matters/${matterId}` : undefined,
  );
  const navigationItems = items.map((item) => {
    const remembered = rememberedPages[item.title];
    const firstPage = getFirstSidebarPage(
      item.title,
      hasPagePermission,
      Boolean(currentUser.owner),
    );
    const destination: SidebarDestination | undefined =
      remembered &&
      canRestoreSidebarPage(
        item.title,
        remembered.to,
        hasPagePermission,
        Boolean(currentUser.owner),
      )
        ? remembered
        : firstPage
          ? { to: firstPage, search: {}, hash: "" }
          : undefined;
    return { ...item, destination };
  });
  const [selectedSection, setSelectedSection] = useState<string | null>(null);

  useEffect(() => {
    setSelectedSection(null);
  }, [currentPath, routeSection, scope]);

  const selectedItem =
    items.find((item) => item.title === selectedSection) ??
    items.find((item) => item.title === routeSection) ??
    (isMatterContextPending ? undefined : items[0]);
  const hasPanel = selectedItem
    ? Boolean(selectedItem.submenu)
    : isMatterContextPending;
  const panelOpen = hasPanel && !isCollapsed;
  const resetSelection = useCallback(() => {
    setSelectedSection(null);
  }, []);
  const { deferNavigation, onTransitionEnd } = useSidebarNavigation({
    panelOpen,
    isMobile,
    scope,
    onCancel: resetSelection,
  });

  return (
    <Sidebar
      collapsible="icon"
      compact={!hasPanel}
      className="z-[40]"
      onTransitionEnd={onTransitionEnd}
    >
      <div className="flex h-full min-h-0 overflow-hidden">
        <SidebarSectionRail
          items={navigationItems}
          selectedSection={selectedItem?.title}
          onSelect={(section, destination) => {
            const deferred = deferNavigation(destination);
            setSelectedSection(section);
            return deferred;
          }}
          hasPanel={hasPanel}
        />
        <AnimatePresence initial={false}>
          {panelOpen ? (
            <SidebarSectionPanel
              key="section-panel"
              title={selectedItem?.label ?? selectedItem?.title}
              submenu={selectedItem?.submenu}
            />
          ) : null}
        </AnimatePresence>
      </div>
    </Sidebar>
  );
}
